/**
 * useOnboarding Hook
 * 
 * Manages onboarding state and progress tracking.
 * Stores progress in the user's Firestore document.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../context/AuthContext'
import { USERS_COLLECTION } from '../services/users'

// Onboarding step definitions
export const ONBOARDING_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to Plot Planet',
    description: 'Build your story universe with characters, events, timelines, and more. Let\'s get you started!',
    target: null, // No target element - centered modal
    position: 'center',
    action: 'next',
  },
  {
    id: 'create-project',
    title: 'Create Your First Project',
    description: 'A project is your story universe container. Create one to start organizing your characters, events, and locations.',
    target: '[data-onboarding="create-project"]',
    position: 'bottom',
    action: 'create',
    skipCondition: 'hasProject',
  },
  {
    id: 'add-character',
    title: 'Add a Character',
    description: 'Characters are the heart of your story. Add your first character with a name, description, and image.',
    target: '[data-onboarding="add-entity"]',
    position: 'left',
    action: 'create',
    skipCondition: 'hasCharacter',
    requiresProject: true,
  },
  {
    id: 'add-event',
    title: 'Create an Event',
    description: 'Events are key moments in your story\'s timeline. Add important plot points, turning points, or milestones.',
    target: '[data-onboarding="add-entity"]',
    position: 'left',
    action: 'create',
    skipCondition: 'hasEvent',
    requiresProject: true,
  },
  {
    id: 'view-timeline',
    title: 'Explore the Timeline',
    description: 'Your events appear on an interactive timeline. Zoom, pan, and click events to see details.',
    target: '[data-onboarding="timeline"]',
    position: 'bottom',
    action: 'next',
    requiresProject: true,
  },
  {
    id: 'complete',
    title: 'You\'re Ready!',
    description: 'You\'ve got the basics! Explore more features like locations, shots, storyboards, and AI-assisted writing.',
    target: null,
    position: 'center',
    action: 'finish',
  },
]

/**
 * Hook for managing onboarding flow
 * 
 * @returns {object} Onboarding state and methods
 */
export function useOnboarding() {
  const { user } = useAuth()
  const [onboardingData, setOnboardingData] = useState(null)
  const [loading, setLoading] = useState(true)

  // Subscribe to onboarding data
  useEffect(() => {
    if (!user?.uid) {
      setOnboardingData(null)
      setLoading(false)
      return
    }

    const userRef = doc(db, USERS_COLLECTION, user.uid)

    const unsubscribe = onSnapshot(
      userRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data()
          setOnboardingData(data.onboarding || null)
        } else {
          setOnboardingData(null)
        }
        setLoading(false)
      },
      (err) => {
        console.error('Error fetching onboarding data:', err)
        setLoading(false)
      }
    )

    return unsubscribe
  }, [user?.uid])

  /**
   * Initialize onboarding for a new user
   */
  const startOnboarding = useCallback(async () => {
    if (!user?.uid) return

    const userRef = doc(db, USERS_COLLECTION, user.uid)
    await setDoc(userRef, {
      onboarding: {
        started: true,
        completed: false,
        currentStep: 0,
        completedSteps: [],
        skipped: false,
        startedAt: new Date(),
        lastSeenAt: new Date(),
      }
    }, { merge: true })
  }, [user?.uid])

  /**
   * Complete the current step and advance
   */
  const completeStep = useCallback(async (stepId) => {
    if (!user?.uid || !onboardingData) return

    const currentIndex = ONBOARDING_STEPS.findIndex(s => s.id === stepId)
    const completedSteps = [...(onboardingData.completedSteps || []), stepId]
    const nextStep = currentIndex + 1

    const userRef = doc(db, USERS_COLLECTION, user.uid)
    await setDoc(userRef, {
      onboarding: {
        ...onboardingData,
        completedSteps,
        currentStep: nextStep,
        lastSeenAt: new Date(),
        completed: nextStep >= ONBOARDING_STEPS.length,
      }
    }, { merge: true })
  }, [user?.uid, onboardingData])

  /**
   * Skip to the next step without completing
   */
  const skipStep = useCallback(async () => {
    if (!user?.uid || !onboardingData) return

    const nextStep = (onboardingData.currentStep || 0) + 1

    const userRef = doc(db, USERS_COLLECTION, user.uid)
    await setDoc(userRef, {
      onboarding: {
        ...onboardingData,
        currentStep: nextStep,
        lastSeenAt: new Date(),
        completed: nextStep >= ONBOARDING_STEPS.length,
      }
    }, { merge: true })
  }, [user?.uid, onboardingData])

  /**
   * Skip/dismiss all of onboarding
   */
  const dismissOnboarding = useCallback(async () => {
    if (!user?.uid) return

    const userRef = doc(db, USERS_COLLECTION, user.uid)
    await setDoc(userRef, {
      onboarding: {
        ...(onboardingData || {}),
        completed: true,
        skipped: true,
        lastSeenAt: new Date(),
      }
    }, { merge: true })
  }, [user?.uid, onboardingData])

  /**
   * Restart onboarding from the beginning
   */
  const restartOnboarding = useCallback(async () => {
    if (!user?.uid) return

    const userRef = doc(db, USERS_COLLECTION, user.uid)
    await setDoc(userRef, {
      onboarding: {
        started: true,
        completed: false,
        currentStep: 0,
        completedSteps: [],
        skipped: false,
        restartedAt: new Date(),
        lastSeenAt: new Date(),
      }
    }, { merge: true })
  }, [user?.uid])

  // Computed values
  const currentStep = useMemo(() => {
    if (!onboardingData) return null
    const index = onboardingData.currentStep || 0
    return ONBOARDING_STEPS[index] || null
  }, [onboardingData])

  const progress = useMemo(() => {
    if (!onboardingData) return 0
    const current = onboardingData.currentStep || 0
    return Math.round((current / ONBOARDING_STEPS.length) * 100)
  }, [onboardingData])

  const isActive = useMemo(() => {
    return onboardingData?.started && !onboardingData?.completed
  }, [onboardingData])

  const shouldShow = useMemo(() => {
    // Show if user is logged in and onboarding is active
    return user && isActive
  }, [user, isActive])

  const needsOnboarding = useMemo(() => {
    // User needs onboarding if they haven't started yet
    return user && !onboardingData?.started && !onboardingData?.completed
  }, [user, onboardingData])

  return {
    // State
    loading,
    onboardingData,
    currentStep,
    currentStepIndex: onboardingData?.currentStep || 0,
    totalSteps: ONBOARDING_STEPS.length,
    progress,
    
    // Flags
    isActive,
    shouldShow,
    needsOnboarding,
    isCompleted: onboardingData?.completed || false,
    wasSkipped: onboardingData?.skipped || false,
    
    // Methods
    startOnboarding,
    completeStep,
    skipStep,
    dismissOnboarding,
    restartOnboarding,
    
    // Step definitions
    steps: ONBOARDING_STEPS,
  }
}

export default useOnboarding
